<template>
    <article class="playground-container">
        <!-- Modern Header -->
        <div class="section-header">
            <h2 class="section-title">Playground</h2>
            <div class="section-divider">
                <span class="divider-dot"></span>
                <span class="divider-line"></span>
                <span class="divider-dot"></span>
            </div>
            <p class="section-description">A showcase of my personal projects and experiments</p>
        </div>

        <!-- Projects Grid -->
        <section class="playground-section">
            <!-- Empty state message - won't be visible with our current setup but kept for future flexibility -->
            <div v-if="displayProjects.length === 0" class="empty-state">
                <ion-icon name="construct-outline" class="empty-icon"></ion-icon>
                <h3 class="empty-title">Welcome to Your Project Playground</h3>
                <p class="empty-description">This is your space to showcase personal projects and experiments. Start building your collection by adding your first project.</p>
                <div class="empty-state-actions">
                    <button class="add-project-btn" @click="addTemplateProjects">
                        <ion-icon name="add-circle-outline"></ion-icon>
                        Add Template Projects
                    </button>
                    <button v-if="projectData && projectData.length > 0" class="load-samples-btn" @click="loadProjectsFromProps">
                        <ion-icon name="download-outline"></ion-icon>
                        Load Sample Projects
                    </button>
                </div>
            </div>
            
            <!-- Projects grid -->
            <div class="playground-grid" v-else>
                <!-- Project card templates with clear IDs for easy updating -->
                <div class="project-card" v-for="(project, index) in displayProjects" :key="index"
                     :class="`project-${index + 1}`" 
                     :style="{ animationDelay: `${index * 0.1}s` }">
                    <div class="project-image">
                        <!-- To update image: Replace "default-project.jpg" with your image filename in data() -->
                        <img :src="getProjectImage(project.image)" :alt="project.title" loading="lazy" @error="handleImageError">
                        <div class="project-badge" v-if="project.status">{{ project.status }}</div>
                    </div>
                    <div class="project-content">
                        <h4 class="project-title">{{ project.title }}</h4>
                        <p class="project-description">{{ project.description }}</p>
                        <!-- Technology tags - easily editable in data() -->
                        <div class="project-tech">
                            <span class="project-tech-item" v-for="(tech, idx) in project.technologies" :key="idx">
                                {{ tech }}
                            </span>
                        </div>
                        <!-- Action links - update URLs in data() -->
                        <div class="project-links">
                            <a v-if="project.demoUrl" :href="project.demoUrl" class="project-link demo-link" target="_blank" rel="noopener noreferrer">
                                <ion-icon name="play-outline"></ion-icon>
                                <span>Demo</span>
                            </a>
                            <a v-if="project.codeUrl" :href="project.codeUrl" class="project-link code-link" target="_blank" rel="noopener noreferrer">
                                <ion-icon name="logo-github"></ion-icon>
                                <span>Code</span>
                            </a>
                        </div>
                    </div>
                </div>

                <!-- Add a 7th project button - will appear when less than 6 projects exist -->
                <template v-if="displayProjects.length < 6">
                    <div class="project-card placeholder-card"
                         v-for="i in (6 - displayProjects.length)" :key="`placeholder-${i}`"
                         @click="addNewProject">
                        <div class="placeholder-content">
                            <ion-icon name="add-circle-outline" class="add-icon"></ion-icon>
                            <p class="placeholder-text">Add Project</p>
                        </div>
                    </div>
                </template>
            </div>
        </section>
        
        <!-- Floating particles for visual appeal -->
        <div class="floating-particles">
            <div v-for="i in 6" :key="i" class="particle" :style="getParticleStyle(i)"></div>
        </div>
    </article>
</template>

<script>
import { getImagePathCompany, getImagePathProject } from '@/utils/images/imageLoader.js';
import { generateBase64UtilImage, generateJsonUtilImage, generateQrUtilImage } from '@/utils/images/projectImageGenerator.js';

export default {
    name: 'PlayGround',
    props: {
        projectData: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            localProjectData: [
                {
                    title: "JSON Utility Tool",
                    description: "A comprehensive web application for working with JSON data. Features include JSON diff comparison, beautify/minify formatting, escape/unescape tools, object conversion, validation, and JSONPath querying.",
                    image: "json.jpg",
                    technologies: ["Vue 3", "TypeScript", "Vite", "CSS3"],
                    demoUrl: "https://jsonhelper.pages.dev/",
                    codeUrl: "https://github.com/whatley95/json-util",
                    status: "Complete"
                },
                {
                    title: "Base64 Util",
                    description: "A modern, fast, and secure utility for converting files to base64 encoding and decoding base64 strings back to files. Features smart file type detection, drag & drop interface, and web worker processing for large files.",
                    image: "generated:base64-util",
                    technologies: ["Next.js", "TypeScript", "Tailwind CSS", "Web Workers"],
                    demoUrl: "https://base64util.pages.dev/",
                    codeUrl: "https://github.com/whatley95/base64-util",
                    status: "Complete"
                },
                {
                    title: "Portfolio Website", 
                    description: "This very portfolio website built with Vue 3. Features responsive design, smooth animations, project showcase, and modern UI components with glass morphism effects.",
                    image: "generated:portfolio",
                    technologies: ["Vue 3", "JavaScript", "CSS3", "Firebase"],
                    demoUrl: window.location.origin, // Points to current site
                    codeUrl: null, // Private repository - code not publicly available
                    status: "Complete"
                },
                {
                    title: "QR Code Utility",
                    description: "An advanced QR code generator and reader with customization options. Create branded QR codes with logos, custom colors, and download in multiple formats, or scan QR codes from images and camera.",
                    image: "generated:qr-util",
                    technologies: ["Angular", "TypeScript", "Angular Material", "ngx-qrcode"],
                    demoUrl: "https://qrutil.pages.dev/",
                    codeUrl: "https://github.com/whatley95/qr-util",
                    status: "Complete"
                },
                {
                    title: "Mobile Task Manager",
                    description: "Cross-platform mobile application for task management with offline support, push notifications, team collaboration, and synchronized data across devices.",
                    image: "generated:mobile-app",
                    technologies: ["React Native", "SQLite", "Push Notifications", "Redux"],
                    demoUrl: "#",
                    codeUrl: "#",
                    status: "In Progress"
                },
                {
                    title: "API Gateway Service",
                    description: "Microservices API gateway with rate limiting, authentication, load balancing, and comprehensive logging for enterprise-level applications.",
                    image: "generated:api-service",
                    technologies: ["Node.js", "Express", "Redis", "Docker"],
                    demoUrl: "#",
                    codeUrl: "#",
                    status: "Coming Soon"
                }
            ]
        };
    },
    computed: {
        displayProjects() {
            return this.localProjectData;
        }
    },
    methods: {
        getImagePathCompany,
        getImagePathProject,
        
        // Enhanced image path handler for generated images
        getProjectImage(imagePath) {
            if (imagePath.startsWith('generated:')) {
                const imageType = imagePath.replace('generated:', '');
                return this.generateProjectImage(imageType);
            }
            return getImagePathProject(imagePath);
        },
        
        // Generate project images based on type
        generateProjectImage(type) {
            try {
                switch (type) {
                    case 'base64-util':
                        return generateBase64UtilImage();
                    case 'json-util':
                        return generateJsonUtilImage();
                    case 'qr-util':
                        return generateQrUtilImage();
                    case 'portfolio':
                        return this.generatePortfolioImage();
                    case 'mobile-app':
                        return this.generateMobileAppImage();
                    case 'api-service':
                        return this.generateApiServiceImage();
                    default:
                        return getImagePathProject('projects/default-project.jpg');
                }
            } catch (error) {
                console.warn('Failed to generate image:', error);
                return getImagePathProject('projects/default-project.jpg');
            }
        },
        
        // Individual image generators with portfolio-specific styling
        generatePortfolioImage() {
            const canvas = document.createElement('canvas');
            canvas.width = 400;
            canvas.height = 250;
            const ctx = canvas.getContext('2d');

            // Portfolio gradient matching your site theme
            const gradient = ctx.createLinearGradient(0, 0, 400, 250);
            gradient.addColorStop(0, '#667eea');
            gradient.addColorStop(0.5, '#764ba2');
            gradient.addColorStop(1, '#f093fb');
            
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, 400, 250);

            // Glass morphism effect
            ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
            ctx.fillRect(0, 0, 400, 250);

            // Central design
            ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.fillRect(60, 60, 280, 130);
            
            // Border
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.lineWidth = 2;
            ctx.strokeRect(60, 60, 280, 130);

            // Portfolio icon
            ctx.fillStyle = '#667eea';
            ctx.font = 'bold 40px Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('</>', 200, 120);

            // Title
            ctx.fillStyle = '#2d3748';
            ctx.font = 'bold 20px Arial, sans-serif';
            ctx.fillText('Portfolio', 200, 150);
            
            // Subtitle
            ctx.font = '12px Arial, sans-serif';
            ctx.fillStyle = '#4a5568';
            ctx.fillText('Vue 3 • Modern Design', 200, 170);

            return canvas.toDataURL('image/jpeg', 0.9);
        },
        
        generateReactDashboardImage() {
            const canvas = document.createElement('canvas');
            canvas.width = 400;
            canvas.height = 250;
            const ctx = canvas.getContext('2d');

            // Dashboard gradient
            const gradient = ctx.createLinearGradient(0, 0, 400, 250);
            gradient.addColorStop(0, '#1e3c72');
            gradient.addColorStop(0.5, '#2a5298');
            gradient.addColorStop(1, '#00b4db');
            
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, 400, 250);

            // Dashboard mockup
            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.fillRect(40, 40, 320, 170);
            
            // Header
            ctx.fillStyle = '#2d3748';
            ctx.fillRect(40, 40, 320, 35);
            
            // Charts
            ctx.fillStyle = '#f7fafc';
            ctx.fillRect(60, 95, 100, 50);
            ctx.fillRect(180, 95, 100, 50);
            ctx.fillRect(300, 95, 40, 50);

            // Chart lines
            ctx.strokeStyle = '#4299e1';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(70, 135);
            ctx.lineTo(85, 115);
            ctx.lineTo(100, 105);
            ctx.lineTo(115, 125);
            ctx.lineTo(130, 100);
            ctx.stroke();

            // Title
            ctx.fillStyle = 'white';
            ctx.font = 'bold 14px Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('Dashboard', 200, 62);

            return canvas.toDataURL('image/jpeg', 0.9);
        },
        
        generateMobileAppImage() {
            const canvas = document.createElement('canvas');
            canvas.width = 400;
            canvas.height = 250;
            const ctx = canvas.getContext('2d');

            // Mobile gradient
            const gradient = ctx.createLinearGradient(0, 0, 400, 250);
            gradient.addColorStop(0, '#ff9a9e');
            gradient.addColorStop(0.5, '#fecfef');
            gradient.addColorStop(1, '#fecfef');
            
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, 400, 250);

            // Phone frame
            ctx.fillStyle = '#2d3748';
            ctx.fillRect(160, 30, 80, 190);
            
            // Screen
            ctx.fillStyle = 'white';
            ctx.fillRect(170, 45, 60, 160);
            
            // Status bar
            ctx.fillStyle = '#4299e1';
            ctx.fillRect(170, 45, 60, 15);
            
            // App content
            ctx.fillStyle = '#f7fafc';
            ctx.fillRect(175, 70, 50, 10);
            ctx.fillRect(175, 90, 50, 10);
            ctx.fillRect(175, 110, 50, 10);
            ctx.fillRect(175, 130, 50, 10);

            // Navigation
            ctx.fillStyle = '#e2e8f0';
            ctx.fillRect(170, 185, 60, 20);

            return canvas.toDataURL('image/jpeg', 0.9);
        },
        
        generateApiServiceImage() {
            const canvas = document.createElement('canvas');
            canvas.width = 400;
            canvas.height = 250;
            const ctx = canvas.getContext('2d');

            // API gradient
            const gradient = ctx.createLinearGradient(0, 0, 400, 250);
            gradient.addColorStop(0, '#134e5e');
            gradient.addColorStop(0.5, '#71b280');
            gradient.addColorStop(1, '#134e5e');
            
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, 400, 250);

            // Terminal
            ctx.fillStyle = '#1a202c';
            ctx.fillRect(60, 60, 280, 130);
            
            // Terminal header
            ctx.fillStyle = '#2d3748';
            ctx.fillRect(60, 60, 280, 25);

            // API text
            ctx.fillStyle = '#68d391';
            ctx.font = '11px monospace';
            ctx.textAlign = 'left';
            ctx.fillText('GET /api/users     200', 80, 110);
            ctx.fillText('POST /api/auth    201', 80, 125);
            ctx.fillText('PUT /api/data     200', 80, 140);
            ctx.fillText('DELETE /cache     204', 80, 155);

            return canvas.toDataURL('image/jpeg', 0.9);
        },
        
        handleImageError(e) {
            // Replace broken image with a placeholder gradient background
            const target = e.target;
            target.src = ''; // Clear the src to stop further attempts to load
            target.classList.add('project-image-placeholder');
            
            // Add title text as a fallback
            const parent = target.closest('.project-image');
            if (parent) {
                const projectCard = parent.closest('.project-card');
                const titleElement = projectCard && projectCard.querySelector('.project-title');
                const title = titleElement ? titleElement.textContent.trim() : 'Project';
                
                // Create fallback text element if it doesn't exist
                if (!parent.querySelector('.image-fallback-text')) {
                    const fallbackText = document.createElement('div');
                    fallbackText.className = 'image-fallback-text';
                    fallbackText.textContent = title;
                    parent.appendChild(fallbackText);
                }
            }
        },
        
        getParticleStyle(index) {
            const positions = [
                { top: '10%', left: '5%', animationDelay: '0s' },
                { top: '35%', right: '8%', animationDelay: '3s' },
                { top: '60%', left: '3%', animationDelay: '6s' },
                { top: '85%', right: '15%', animationDelay: '2s' },
                { top: '25%', left: '90%', animationDelay: '4s' },
                { top: '70%', right: '85%', animationDelay: '1s' }
            ];
            return positions[index - 1] || {};
        },
        
        addProject(project) {
            // Method to easily add a new project to the playground
            if (!project.image) {
                project.image = "projects/default-project.jpg"; // Default image if none provided
            }
            
            this.localProjectData.push(project);
            
            // Emit event to notify parent component
            this.$emit('project-added', [...this.localProjectData]);
        },
        
        loadProjectsFromProps() {
            // Use this method to load projects from props (profile.json)
            if (this.projectData && this.projectData.length > 0) {
                this.localProjectData = [...this.projectData];
                return true;
            }
            return false;
        },
        
        addTemplateProjects() {
            // Add template projects to show a 3x2 grid of coming soon/in progress projects
            this.localProjectData = [
                {
                    title: "JSON Utility Tool",
                    description: "A comprehensive web application for working with JSON data. Features include JSON diff comparison, beautify/minify formatting, escape/unescape tools, object conversion, validation, and JSONPath querying.",
                    image: "json.jpg",
                    technologies: ["Vue 3", "TypeScript", "Vite", "CSS3"],
                    demoUrl: "https://jsonhelper.pages.dev/",
                    codeUrl: "https://github.com/whatley95/json-util",
                    status: "Complete"
                },
                {
                    title: "Base64 Util",
                    description: "A modern, fast, and secure utility for converting files to base64 encoding and decoding base64 strings back to files. Features smart file type detection, drag & drop interface, and web worker processing for large files.",
                    image: "generated:base64-util",
                    technologies: ["Next.js", "TypeScript", "Tailwind CSS", "Web Workers"],
                    demoUrl: "https://base64util.pages.dev/",
                    codeUrl: "https://github.com/whatley95/base64-util",
                    status: "Complete"
                },
                {
                    title: "Portfolio Website", 
                    description: "This very portfolio website built with Vue 3. Features responsive design, smooth animations, project showcase, and modern UI components with glass morphism effects.",
                    image: "generated:portfolio",
                    technologies: ["Vue 3", "JavaScript", "CSS3", "Firebase"],
                    demoUrl: window.location.origin, // Points to current site
                    codeUrl: null, // Private repository - code not publicly available
                    status: "Complete"
                },
                {
                    title: "QR Code Utility",
                    description: "An advanced QR code generator and reader with customization options. Create branded QR codes with logos, custom colors, and download in multiple formats, or scan QR codes from images and camera.",
                    image: "generated:qr-util",
                    technologies: ["Angular", "TypeScript", "Angular Material", "ngx-qrcode"],
                    demoUrl: "https://qrutil.pages.dev/",
                    codeUrl: "https://github.com/whatley95/qr-util",
                    status: "Complete"
                },
                {
                    title: "Mobile Task Manager",
                    description: "Cross-platform mobile application for task management with offline support, push notifications, team collaboration, and synchronized data across devices.",
                    image: "generated:mobile-app",
                    technologies: ["React Native", "SQLite", "Push Notifications", "Redux"],
                    demoUrl: "#",
                    codeUrl: "#",
                    status: "In Progress"
                },
                {
                    title: "API Gateway Service",
                    description: "Microservices API gateway with rate limiting, authentication, load balancing, and comprehensive logging for enterprise-level applications.",
                    image: "generated:api-service",
                    technologies: ["Node.js", "Express", "Redis", "Docker"],
                    demoUrl: "#",
                    codeUrl: "#",
                    status: "Coming Soon"
                }
            ];
        },
        
        addNewProject() {
            // Add a new empty project template when clicking on a placeholder
            const nextProjectNumber = this.localProjectData.length + 1;
            const newProject = {
                title: `Project ${nextProjectNumber}`,
                description: "Coming soon - loading",
                image: "projects/default-project.jpg", // Keep default for new projects
                technologies: ["Tech 1", "Tech 2", "Tech 3"],
                demoUrl: "#",
                codeUrl: "#",
                status: "Coming Soon"
            };
            
            this.addProject(newProject);
        }
    }
}
</script>

<style scoped>
.playground-container {
    padding: 3rem 1rem;
    position: relative;
    overflow: hidden;
    width: 100%;
    margin: 0 auto;
}

/* Section Header Styling */
.section-header {
    text-align: center;
    margin-bottom: 4rem;
}

.section-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--white-1);
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.section-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1.5rem 0;
    gap: 1rem;
}

.divider-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.divider-line {
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #667eea, transparent);
}

.section-description {
    color: var(--light-gray-70);
    font-size: 1.1rem;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
}

/* Playground Section */
.playground-section {
    margin-bottom: 4rem;
}

.playground-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
    padding: 1rem;
    width: 100%;
    margin: 0 auto;
}

.project-card {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    overflow: hidden;
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    opacity: 0;
    animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.project-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
    border-color: rgba(102, 126, 234, 0.3);
}

.project-image {
    height: 180px;
    overflow: hidden;
    position: relative;
}

.project-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
}

.project-card:hover .project-image img {
    transform: scale(1.05);
}

.project-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.3rem 0.8rem;
    border-radius: 20px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.project-content {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
}

.project-title {
    color: var(--white-1);
    font-size: 1.3rem;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 0.8rem;
    line-height: 1.4;
}

.project-description {
    color: var(--light-gray-70);
    font-size: 0.95rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    flex-grow: 1;
}

.project-tech {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
}

.project-tech-item {
    background: rgba(102, 126, 234, 0.15);
    color: #b0b9ec;
    padding: 0.2rem 0.6rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    border: 1px solid rgba(102, 126, 234, 0.3);
}

.project-links {
    display: flex;
    gap: 1rem;
    margin-top: auto;
}

.project-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    text-decoration: none;
    border: 1px solid;
}

.demo-link {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
    border-color: rgba(102, 126, 234, 0.3);
}

.demo-link:hover {
    background: rgba(102, 126, 234, 0.2);
    transform: translateY(-2px);
}

.code-link {
    background: rgba(255, 255, 255, 0.05);
    color: var(--light-gray-70);
    border-color: rgba(255, 255, 255, 0.1);
}

.code-link:hover {
    background: rgba(255, 255, 255, 0.1);
    color: var(--white-2);
    transform: translateY(-2px);
}

.placeholder-card {
    border: 2px dashed rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: transparent;
    box-shadow: none;
    transition: all 0.3s ease;
}

.placeholder-card:hover {
    border-color: rgba(102, 126, 234, 0.5);
    background: rgba(255, 255, 255, 0.03);
}

.placeholder-content {
    text-align: center;
    padding: 2rem;
}

.add-icon {
    font-size: 2.5rem;
    color: rgba(255, 255, 255, 0.3);
    margin-bottom: 0.5rem;
    transition: color 0.3s ease;
}

.placeholder-card:hover .add-icon {
    color: rgba(102, 126, 234, 0.7);
}

.placeholder-text {
    color: rgba(255, 255, 255, 0.5);
    font-size: 1rem;
    margin: 0;
    transition: color 0.3s ease;
}

.placeholder-card:hover .placeholder-text {
    color: rgba(255, 255, 255, 0.8);
}

/* Floating particles */
.floating-particles {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: -1;
    overflow: hidden;
}

.particle {
    position: absolute;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: radial-gradient(circle at center, rgba(102, 126, 234, 0.5), rgba(102, 126, 234, 0));
    opacity: 0.4;
    animation: float 15s infinite linear;
}

@keyframes float {
    0% {
        transform: translateY(0) rotate(0deg) scale(1);
        opacity: 0.4;
    }
    50% {
        transform: translateY(-20px) rotate(180deg) scale(1.5);
        opacity: 0.6;
    }
    100% {
        transform: translateY(0) rotate(360deg) scale(1);
        opacity: 0.4;
    }
}

/* Responsive adjustments */
@media (max-width: 992px) {
    .playground-grid {
        grid-template-columns: repeat(2, 1fr);
        padding: 0.5rem;
    }
    
    .project-image {
        height: 160px;
    }
    
    .project-content {
        padding: 1.25rem;
    }
    
    .project-title {
        font-size: 1.2rem;
    }
    
    .project-description {
        font-size: 0.9rem;
    }
}

@media (max-width: 768px) {
    .playground-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 480px) {
    .playground-grid {
        grid-template-columns: 1fr;
    }
    
    .project-image {
        height: 200px;
    }
}

/* Empty state styling */
.empty-state {
    text-align: center;
    padding: 3rem;
    background: rgba(255, 255, 255, 0.03);
    border-radius: 16px;
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    max-width: 600px;
    margin: 2rem auto;
}

.empty-icon {
    font-size: 4rem;
    color: rgba(102, 126, 234, 0.6);
    margin-bottom: 1rem;
    display: inline-block;
}

.empty-title {
    font-size: 1.8rem;
    font-weight: 600;
    color: var(--white-1);
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.empty-description {
    color: var(--light-gray-70);
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 2rem;
    max-width: 450px;
    margin-left: auto;
    margin-right: auto;
}

.empty-state-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
}

.add-project-btn, .load-samples-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.8rem 1.5rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.95rem;
    transition: all 0.3s ease;
    border: none;
    cursor: pointer;
}

.add-project-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.load-samples-btn {
    background: rgba(255, 255, 255, 0.1);
    color: var(--light-gray-80);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.add-project-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.5);
}

.load-samples-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-3px);
}

/* Placeholder styles for missing images */
.project-image-placeholder {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.image-fallback-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-weight: bold;
    text-align: center;
    padding: 1rem;
    width: 80%;
    font-size: 1.2rem;
    text-shadow: 0 1px 3px rgba(0,0,0,0.3);
}
</style>
